import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { signUp, sendVerificationEmail } from '../services/firebase/auth';
import { createClimber } from '../services/firebase/climber';
import EmailVerificationCheck from '../components/EmailVerificationCheck';
import SEO from '../components/SEO';
import '../styles/Auth.css';

const Signup: React.FC = () => {
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [language, setLanguage] = useState('EN'); // Default to English
  const [verificationSent, setVerificationSent] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      return setError("Passwords don't match");
    }
    
    try {
      setError('');
      setLoading(true);
      
      // Create the user in Firebase Authentication
      const user = await signUp(email, password, displayName);
      
      // Create a Climber profile in Firestore
      if (user && user.uid) {
        await createClimber(user.uid, displayName, displayName, language);
        
        // Redirect to confirm email page
        navigate('/confirm-email');
      } else {
        navigate('/');
      }
    } catch (err: any) {
      setError('Failed to create account: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };
  
  // Handle resending verification email
  const handleResendVerification = async () => {
    try {
      setLoading(true);
      await sendVerificationEmail();
      setError('');
      // Show a success message
      alert('Verification email resent. Please check your inbox.');
    } catch (err: any) {
      setError('Failed to resend verification email: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };
  
  // Handle proceeding to the next screen
  const proceedToNextScreen = () => {
    navigate('/gyms');
  };

  // Handle navigation to login
  const navigateToLogin = () => {
    navigate('/login');
  };

  // Check if user is on mobile device
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  // Update isMobile state when window is resized
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      <SEO 
        title="Sign Up - Banana Cup Climbing App | Join Climbing Community"
        description="Create your Banana Cup climbing app account to start tracking your climbing progress, logging routes, and joining the paraclimbing community. Free climbing logger with heart rate monitor support."
        keywords="signup climbing app, create climbing account, join banana cup, paraclimbing signup, climbing app registration, bananacup.be signup"
        path="/signup"
      />
      <div 
        className={`auth-container ${isMobile ? 'mobile-view' : ''}`}
      >
      <div className="auth-banner">
        <img src={require('../assets/images/banana-illustration.webp')} alt="Banana" />
        <h1>The Banana Cup</h1>
        <p>Made by Paraclimbing.be</p>
        {!verificationSent && (
          <div className="language-selector">
            <label htmlFor="language-select">Language</label>
            <select 
              id="language-select" 
              value={language} 
              onChange={(e) => setLanguage(e.target.value)}
              className="language-dropdown"
            >
              <option value="EN">English</option>
              <option value="NL">Nederlands</option>
              <option value="FR">Français</option>
            </select>
          </div>
        )}
        {!verificationSent && (
          <button 
            onClick={navigateToLogin} 
            className="auth-button secondary-button"
          >
            Go to Login
          </button>
        )}
      </div>
      {/* For mobile view, show verification screen if needed */}
      {isMobile && verificationSent && (
        <div className="auth-content mobile-view">
          <div className="auth-card">
            <h2 className="auth-title">Email Verification</h2>
            <EmailVerificationCheck 
              onVerified={() => navigate('/gyms')}
              redirectPath="/gyms"
            />
          </div>
        </div>
      )}
      {!isMobile && (
        <div className="auth-content">
          <div className="auth-card">
            <h2 className="auth-title">Sign Up</h2>
            {error && <div className="auth-error">{error}</div>}
          {verificationSent ? (
            <EmailVerificationCheck 
              onVerified={() => navigate('/gyms')}
              redirectPath="/gyms"
            />
          ) : (
            <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="displayName">Name</label>
              <input 
                type="text" 
                id="displayName" 
                value={displayName} 
                onChange={(e) => setDisplayName(e.target.value)} 
                required 
              />
            </div>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input 
                type="email" 
                id="email" 
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
                required 
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input 
                type="password" 
                id="password" 
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
                required 
              />
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input 
                type="password" 
                id="confirmPassword" 
                value={confirmPassword} 
                onChange={(e) => setConfirmPassword(e.target.value)} 
                required 
              />
            </div>
            <button disabled={loading} className="auth-button" type="submit">
              {loading ? 'Creating Account...' : 'Sign Up'}
            </button>
          </form>
          )}
          <div className="auth-links">
            <p>
              Already have an account? <Link to="/login">Log In</Link>
            </p>
          </div>
        </div>
      </div>
      )}
    </div>
    </>
  );
};

export default Signup;